import React, { createContext, useContext, useState, useCallback, type ReactNode } from "react";

interface SidebarContextType {
  openSidebar: (content: ReactNode, header?: ReactNode, side?: "left" | "right") => void;
  closeSidebar: () => void;
  isOpen: boolean;
  side: "left" | "right";
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const SidebarProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [content, setContent] = useState<ReactNode>(null);
  const [header, setHeader] = useState<ReactNode>(null);
  const [side, setSide] = useState<"left" | "right">("left");

  const openSidebar = useCallback(
    (sidebarContent: ReactNode, sidebarHeader?: ReactNode, sidebarSide: "left" | "right" = "left") => {
      setContent(sidebarContent);
      setHeader(sidebarHeader || null);
      setSide(sidebarSide);
      setIsOpen(true);
    },
    []
  );

  const closeSidebar = useCallback(() => {
    setIsOpen(false);
    setTimeout(() => {
      setContent(null);
      setHeader(null);
    }, 300);
  }, []);

  return (
    <SidebarContext.Provider value={{ openSidebar, closeSidebar, isOpen, side }}>
      {children}

      {/* Overlay */}
      <div className={`fixed inset-0 z-[9998] ${isOpen ? "pointer-events-auto" : "pointer-events-none"}`}>
        {isOpen && (
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
            onClick={closeSidebar}
          />
        )}

        {/* Sidebar Panel */}
        <div
          className={`
            fixed top-0 h-full w-[240px] max-w-[90vw]
            bg-white dark:bg-gray-900 shadow-xl z-[9999] flex flex-col overflow-hidden
            transform transition-transform duration-300
            ${side === "left" ? (isOpen ? "translate-x-0 left-0" : "-translate-x-full left-0") : ""}
            ${side === "right" ? (isOpen ? "translate-x-0 right-0" : "translate-x-full right-0") : ""}
          `}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          {header && (
            <div className="h-[55px] flex items-center justify-between px-4 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
              <div className="font-medium text-gray-900 dark:text-gray-100">{header}</div>
              <button
                onClick={closeSidebar}
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 text-xl"
              >
                ✕
              </button>
            </div>
          )}

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">{content}</div>
        </div>
      </div>
    </SidebarContext.Provider>
  );
};

export const useSidebar = (): SidebarContextType => {
  const context = useContext(SidebarContext);
  if (!context) throw new Error("useSidebar must be used within SidebarProvider");
  return context;
};